import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { buildOGTags } from "./utils";
import { self } from "@default/js/config";
import { useSelector } from "react-redux";

/**
 * OpenGraphMeta component to implement the open graph protocol
 * @see https://ogp.me/
 */
const OpenGraphMeta = (props) => {
  const microSite = useSelector((reduxStore) => reduxStore.appStore.microSite);

  const propsWithFallback = {
    ...props,
    image: props.image ?? {
      url:
        self + microSite === "rbx-company"
          ? "/rbx-og-fallback.jpg"
          : "/rbf-og-fallback.jpg",
    },
  };
  const tags = buildOGTags(propsWithFallback);

  return (
    <Helmet>
      {tags.map(({ property, content }, i) => (
        <meta key={`${property}-${i}`} property={property} content={content} />
      ))}
    </Helmet>
  );
};

const mediaPropType = PropTypes.shape({
  url: PropTypes.string.isRequired,
  secureUrl: PropTypes.string,
  type: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
});

OpenGraphMeta.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,

  description: PropTypes.string,
  siteName: PropTypes.string,
  locale: PropTypes.string,
  determiner: PropTypes.oneOf(["a", "an", "the", "", "auto"]),
  alternateLocales: PropTypes.arrayOf(PropTypes.string),

  image: PropTypes.oneOfType([
    PropTypes.shape({ ...mediaPropType.propTypes, alt: PropTypes.string }),
    PropTypes.arrayOf(
      PropTypes.shape({ ...mediaPropType.propTypes, alt: PropTypes.string })
    ),
  ]),
  video: PropTypes.oneOfType([mediaPropType, PropTypes.arrayOf(mediaPropType)]),
  audio: PropTypes.oneOfType([
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      secureUrl: PropTypes.string,
      type: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        secureUrl: PropTypes.string,
        type: PropTypes.string,
      })
    ),
  ]),

  articleMeta: PropTypes.shape({
    publishedTime: PropTypes.string,
    modifiedTime: PropTypes.string,
    expirationTime: PropTypes.string,
    author: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    section: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
  bookMeta: PropTypes.shape({
    author: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    isbn: PropTypes.string,
    releaseDate: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
  profileMeta: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    username: PropTypes.string,
    gender: PropTypes.oneOf(["male", "female"]),
  }),
  videoMeta: PropTypes.shape({
    actor: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    actorRole: PropTypes.string,
    director: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    writer: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    duration: PropTypes.number,
    releaseDate: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    series: PropTypes.string,
  }),
  musicSongMeta: PropTypes.shape({
    duration: PropTypes.number,
    album: PropTypes.string,
    albumDisc: PropTypes.number,
    albumTrack: PropTypes.number,
    musician: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }),
  musicAlbumMeta: PropTypes.shape({
    song: PropTypes.string,
    songDisc: PropTypes.number,
    songTrack: PropTypes.number,
    musician: PropTypes.string,
    releaseDate: PropTypes.string,
  }),
};

export default OpenGraphMeta;
